import React, { useState } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
} from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import teamsTabStyles from './teamsTab.styles'
import AssignTeamDropdown from './AssignTeamDropdown/AssignTeamDropdown'
import EditSeasonTeamModal from './EditSeasonTeamModal/EditSeasonTeamModal'
import removeTeamFromGroup from '../../../Apis/groups/removeTeamFromGroup'
import removeTeamFromSeason from '../../../Apis/season/removeTeamFromSeason'
import updateSeasonTeam from '../../../Apis/season/updateSeasonTeam'
import getSeasonTeams from '../../../Apis/season/getSeasonTeams'
import QueryKeys from '../../../utils/constants/queryKeys'

const useStyles = makeStyles(teamsTabStyles)

const TeamsTab = ({ orgId, seasonId, selectedGroup, mode = 'group', gender, ageRestriction, isLoading: isLoadingProp }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const history = useHistory()
  const isSeasonMode = mode === 'season'
  const [editingTeam, setEditingTeam] = useState(null)

  // Season mode: fetch season teams from API
  const { data: seasonTeamsData, isLoading: isLoadingSeasonTeams } = useQuery(
    [QueryKeys.GET_SEASON_TEAMS, orgId, seasonId],
    () => getSeasonTeams(orgId, seasonId),
    {
      enabled: isSeasonMode && Boolean(orgId && seasonId),
    }
  )

  const isLoading = isSeasonMode ? isLoadingSeasonTeams : Boolean(isLoadingProp)

  // Normalize teams to a common shape
  const teams = isSeasonMode
    ? (seasonTeamsData || []).map((t) => ({ seasonTeamId: t.id, teamId: t.teamId, teamName: t.teamName, teamLogoUrl: t.teamLogoUrl }))
    : (selectedGroup?.teams || [])

  const removeFromGroupMutation = useMutation(
    (seasonTeamId) => removeTeamFromGroup({
      orgId,
      groupId: selectedGroup?.id,
      seasonTeamId
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        queryClient.invalidateQueries(QueryKeys.GET_GROUP_TEAMS)
      },
    }
  )

  const removeFromSeasonMutation = useMutation(
    (seasonTeamId) => removeTeamFromSeason({ orgId, seasonTeamId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId])
      },
    }
  )

  const updateSeasonTeamMutation = useMutation(
    ({ seasonTeamId, name }) => updateSeasonTeam({ orgId, seasonTeamId, name }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId])
        setEditingTeam(null)
      },
    }
  )

  const removeMutation = isSeasonMode ? removeFromSeasonMutation : removeFromGroupMutation

  const handleDeleteTeam = (seasonTeamId) => {
    removeMutation.mutate(seasonTeamId)
  }

  const handleTeamClick = (team) => {
    const teamIdParam = team.teamId || team.seasonTeamId
    history.push({
      pathname: `/seasonTeams/${teamIdParam}`,
      search: `?org=${orgId}`,
      state: { seasonTeamId: team.seasonTeamId },
    })
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        {isSeasonMode ? (
          <AssignTeamDropdown
            orgId={orgId}
            seasonId={seasonId}
            mode='season'
            gender={gender}
            ageRestriction={ageRestriction}
          />
        ) : selectedGroup ? (
          <AssignTeamDropdown
            orgId={orgId}
            seasonId={seasonId}
            group={selectedGroup}
            mode='group'
          />
        ) : (
          <Typography variant='body2' color='textSecondary'>
            Please select a group to assign teams
          </Typography>
        )}
      </div>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team Name</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} align='center' style={{ padding: 32 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : teams.length > 0 ? (
              teams.map((team) => (
                <TableRow key={team.seasonTeamId} hover style={{ cursor: 'pointer' }} onClick={() => handleTeamClick(team)}>
                  <TableCell>
                    <div className={classes.teamInfo}>
                      <Avatar
                        src={team.teamLogoUrl}
                        alt={team.teamName}
                        className={classes.teamLogo}
                      >
                        {!team.teamLogoUrl && team.teamName?.charAt(0)}
                      </Avatar>
                      <Typography variant='body2'>
                        {team.teamName}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align='right'>
                    <div className={classes.actionButtons}>
                      {isSeasonMode && (
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={(e) => { e.stopPropagation(); setEditingTeam(team) }}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton
                        size='small'
                        color='secondary'
                        onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team.seasonTeamId) }}
                        disabled={removeMutation.isLoading && removeMutation.variables === team.seasonTeamId}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  {isSeasonMode ? 'No teams assigned to this season' : 'No teams assigned to this group'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditSeasonTeamModal
        open={Boolean(editingTeam)}
        team={editingTeam}
        onClose={() => setEditingTeam(null)}
        onConfirm={(name) => updateSeasonTeamMutation.mutate({ seasonTeamId: editingTeam.seasonTeamId, name })}
        isLoading={updateSeasonTeamMutation.isLoading}
      />
    </div>
  )
}

export default TeamsTab
